import { createContext, useContext, useState, ReactNode, useCallback, useMemo, forwardRef } from 'react';
import { css } from '@emotion/react';
import {
  NotificationType,
  NotificationType_index as NotificationTypeIndex,
} from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { AnimatePresence, motion, MotionProps } from 'motion/react';
import { useQuery, useQueryClient } from 'react-query';
import { NotificationApi, NotificationQueries } from '@frontend/api-notifications';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useAppScopeStore } from '@frontend/scope';
import { allowedNotificationTypes } from '@frontend/settings-routing';
import { WeavePopNotification, CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { NotificationComponent } from './notification-components';
import { useNotificationContext } from './notification-provider';
import { MuteAction } from './shared/mute-action-context';
import { MuteFlowManager } from './shared/muted-pops';
import { NewNotificationsProvider } from './shared/new-notifications-context';
import { Notification } from './shared/notification';
import { OpenNotifications } from './shared/slider';

type Placement = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

export interface TransientQueueProps {
  placement?: Placement;
}

export const TransientQueue = ({ placement = 'top-right' }: TransientQueueProps) => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isExperimentalMode = getFeatureFlagValue('pop-notifications-experiments');
  const isNewNotificationsExperience = getFeatureFlagValue('revamped-notifications-pops');
  const [isMuteFlowActive, setIsMuteFlowActive] = useState(false);
  const [activeNotificationTypeForMute, setActiveNotificationTypeForMute] = useState<
    WeavePopNotification['type'] | null
  >(null);
  const { queue, activeIndex, setActiveIndex, removeAll } = useTransientQueueContext();
  const [x, y] = placement.split('-');
  const ctx = useNotificationContext();

  const initiateMuteFlow = useCallback((type: WeavePopNotification['type']) => {
    setActiveNotificationTypeForMute(type);
    setIsMuteFlowActive(true);
  }, []);

  const handleMuteFlowDismiss = useCallback(() => {
    setIsMuteFlowActive(false);
    setActiveNotificationTypeForMute(null);
  }, []);

  const lastFocusedNotification = queue.find((item) => item.state.paused === true);
  const notificationInView = lastFocusedNotification || queue[activeIndex ?? 0];
  const hasPreference = queue.some((item) => item.type === 'preference');
  const hasSlider = queue.length > 1;

  return (
    <MuteAction.Provider value={initiateMuteFlow}>
      <NewNotificationsProvider value={isNewNotificationsExperience}>
        <MuteFlowOrchestrator
          isActive={isMuteFlowActive}
          notificationType={activeNotificationTypeForMute}
          onDismiss={handleMuteFlowDismiss}
          x={x}
          y={y}
        />
        <AnimatePresence>
          {notificationInView &&
            (isNewNotificationsExperience ? (
              <NewNotificationsExperience
                isPaused={isMuteFlowActive}
                notificationInView={notificationInView}
                x={x}
                y={y}
              />
            ) : isExperimentalMode ? (
              <motion.div
                style={{
                  ...(x === 'top' ? { top: 80 } : { bottom: 0 }),
                  ...(y === 'right' ? { right: 50 } : { left: 0 }),
                  display: 'flex',
                  position: 'fixed',
                  zIndex: theme.zIndex.alerts,
                  flexDirection: 'column',
                  gap: theme.spacing(1),
                  padding: theme.spacing(1),
                }}
                animate={{ top: hasSlider ? 40 : 80 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <Notification
                  notification={notificationInView}
                  onEmit={(action, notification) => {
                    ctx.emit(notification.type, {
                      action: typeof action === 'string' ? action : action.action,
                      payload: typeof action === 'string' ? undefined : action?.payload,
                      notification: notification as any,
                    });
                  }}
                  ContainerComponent={TransientItemContainer}
                  containerProps={{ id: notificationInView.id, xAlignment: 'center' }}
                  containerStyles={css`
                    border-radius: ${theme.borderRadius.medium};
                    box-shadow: ${theme.shadows.heavy};
                    position: relative;
                    width: 350px;
                  `}
                >
                  <Notification.ProgressBar />
                  <AnimatePresence>
                    {queue.length > 1 && (
                      <Notification.Slider
                        disableClearAll={hasPreference}
                        onClearAll={removeAll}
                        currIndex={activeIndex}
                        total={queue.length}
                        onChange={setActiveIndex}
                        queue={queue}
                      />
                    )}
                  </AnimatePresence>
                  <Notification.Content stacked={queue.length > 1} />
                </Notification>
              </motion.div>
            ) : (
              <DefaultNotification notificationInView={notificationInView} x={x} y={y} />
            ))}
        </AnimatePresence>
      </NewNotificationsProvider>
    </MuteAction.Provider>
  );
};

interface MuteFlowOrchestratorProps {
  isActive: boolean;
  notificationType: WeavePopNotification['type'] | null;
  onDismiss: () => void;
  x: string;
  y: string;
}

export const MuteFlowOrchestrator = ({ isActive, notificationType, onDismiss, x, y }: MuteFlowOrchestratorProps) => {
  const { selectedLocationIds } = useAppScopeStore();
  const { mutateAsync: updateNotificationSetting } = NotificationQueries.useNotificationSettingsMutation({});
  const queryClient = useQueryClient();
  const { removeAll } = useTransientQueueContext();

  const { data: notificationSettings } = useQuery({
    queryKey: [selectedLocationIds[0], 'notification-settings', selectedLocationIds[0]],
    queryFn: async () => await NotificationApi.getAllNotificationSettings(selectedLocationIds[0]),
    // enabled: !!selectedLocationIds?.[0],
    // We will re-enable this when we figure out a better way to handle muting notifications
    enabled: false,
  });

  const filteredNotificationSettings = useMemo(
    () => notificationSettings?.filter((setting) => allowedNotificationTypes.find((type) => type.id === setting.id)),
    [notificationSettings]
  );

  const handleConfirmMuteAll = useCallback(async () => {
    removeAll();
    try {
      await Promise.all(
        allowedNotificationTypes.map((notification) =>
          updateNotificationSetting({
            id: NotificationTypeIndex[notification.firestoreType] as unknown as NotificationType,
            locationId: selectedLocationIds?.[0],
            value: false,
          })
        )
      );
    } catch (error) {
      console.error('Failed to update notification settings:', error);
    }
  }, [removeAll, updateNotificationSetting, selectedLocationIds]);

  const handleReverseMuteAll = useCallback(async () => {
    removeAll();
    try {
      if (!filteredNotificationSettings) return;

      await Promise.all(
        filteredNotificationSettings.map((notification) =>
          updateNotificationSetting({
            id: notification.notificationType,
            locationId: selectedLocationIds?.[0],
            value: true,
          })
        )
      );
    } catch (error) {
      console.error('Failed to update notification settings:', error);
    }
  }, [removeAll, updateNotificationSetting, selectedLocationIds, filteredNotificationSettings]);

  const handleMuteFlowCompleted = useCallback(() => {
    onDismiss();
    queryClient.invalidateQueries([selectedLocationIds[0], 'notification-settings', selectedLocationIds[0]]);
  }, [onDismiss, queryClient, selectedLocationIds]);

  const handleManageMuteSettings = useCallback(() => {
    handleReverseMuteAll();
    onDismiss();
  }, [handleReverseMuteAll, onDismiss]);

  if (!isActive || !notificationType) {
    return null;
  }

  return (
    <div
      style={{
        ...(x === 'top' ? { top: 73 } : { bottom: 0 }),
        ...(y === 'right' ? { right: 57 } : { left: 0 }),
        position: 'fixed',
        zIndex: theme.zIndex.alerts + 1,
        padding: theme.spacing(1),
      }}
    >
      <MuteFlowManager
        notificationType={notificationType}
        onMuteConfirmed={handleConfirmMuteAll}
        onFlowCompleted={handleMuteFlowCompleted}
        onFlowDismissed={onDismiss}
        onManageMuteSettings={handleManageMuteSettings}
      />
    </div>
  );
};

const NewNotificationsExperience = ({
  x,
  y,
  isPaused,
}: {
  isPaused: boolean;
  notificationInView: WeavePopNotification;
  x: string;
  y: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { queue, removeAll } = useTransientQueueContext();
  const hasPreference = queue.some((item) => item.type === 'preference');
  const ctx = useNotificationContext();

  return (
    <motion.div
      data-testid='new-notifications-experience-transient-queue'
      style={{
        ...(x === 'top' ? { top: 73 } : { bottom: 0 }),
        ...(y === 'right' ? { right: 57 } : { left: 0 }),
        display: 'flex',
        position: 'fixed',
        zIndex: theme.zIndex.alerts,
        flexDirection: 'column',
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        maxHeight: 'calc(100vh - 73px)',
        overflowY: 'auto',
        paddingBottom: theme.spacing(4),
      }}
      css={css`
        ::before {
          content: '';
          position: absolute;
          top: ${theme.spacing(-1)};
          left: 0;
          width: 100%;
          height: calc(100% - 16px);
          mask-image: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, black 40%, black 100%);
          z-index: -1;
          opacity: ${isExpanded ? 1 : 0};
          transition: opacity 0.2s ease-in-out;
        }
      `}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <AnimatePresence>
        {queue.length > 1 && (
          <OpenNotifications
            isExpanded={isExpanded}
            onExpand={() => setIsExpanded((prev) => !prev)}
            disableClearAll={hasPreference}
            onClearAll={removeAll}
            total={queue.length}
            queue={queue}
          />
        )}
      </AnimatePresence>
      {queue.slice(0, isExpanded ? undefined : 1).map((notificationInView) => (
        <Notification
          isPaused={isExpanded || isPaused}
          key={notificationInView.id}
          notification={notificationInView}
          onEmit={(action, notification) => {
            ctx.emit(notification.type, {
              action: typeof action === 'string' ? action : action.action,
              payload: typeof action === 'string' ? undefined : action?.payload,
              notification: notification as any,
            });
          }}
          ContainerComponent={TransientItemContainer}
          containerProps={{
            id: notificationInView.id,
            xAlignment: 'center',
            'data-testid': 'new-notification-pop-instance',
          }}
          containerStyles={css`
            border-radius: ${theme.borderRadius.medium};
            box-shadow: ${theme.shadows.heavy};
            position: relative;
            width: 350px;
          `}
        >
          {!isExpanded && <Notification.ProgressBarNew />}
          <Notification.Content stacked={queue.length > 1} />
        </Notification>
      ))}
    </motion.div>
  );
};

const DefaultNotification = ({
  notificationInView,
  x,
  y,
}: {
  notificationInView: WeavePopNotification;
  x: string;
  y: string;
}) => {
  const ctx = useNotificationContext();

  return (
    <motion.div
      style={{
        ...(x === 'top' ? { top: 80 } : { bottom: 0 }),
        ...(y === 'right' ? { right: 0 } : { left: 0 }),
        display: 'flex',
        position: 'fixed',
        flexDirection: 'column',
        zIndex: theme.zIndex.alerts,
        gap: theme.spacing(1),
        padding: theme.spacing(1),
      }}
    >
      <TransientItemContainer key={notificationInView.id} id={notificationInView.id} xAlignment={x as 'left' | 'right'}>
        <NotificationComponent
          key={notificationInView.id}
          notification={notificationInView}
          emit={(e, notification) => {
            ctx.emit(notificationInView.type, {
              action: e.action,
              payload: e.payload,
              notification: notification as any, //TODO: @gisheri - try to avoid this typecast
            });
          }}
        />
      </TransientItemContainer>
    </motion.div>
  );
};

export type AddOptions = {
  duration?: number;
};

type TransientQueueContextType = {
  queue: WeavePopNotification[];
  add: (notification: WeavePopNotification, options?: AddOptions) => void;
  remove: (id: string) => void;
  removeAll: () => void;
  update: (notification: WeavePopNotification) => void;
  setActiveIndex: (index: number) => void;
  activeIndex: number;
};

const TransientQueueContext = createContext<TransientQueueContextType>({
  queue: [],
  add: (_item, _options) => {
    console.warn('Notification Queue Not Initialized');
  },
  remove: (_id) => {},
  removeAll: () => {},
  update: (_item) => {},
  setActiveIndex: (_index) => {},
  activeIndex: 0,
});

const useTransientQueueContext = () => useContext(TransientQueueContext);

export const TransientQueueProvider = ({ children }: { children: ReactNode }) => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isExperimentalMode = getFeatureFlagValue('pop-notifications-experiments');
  const [queue, setQueue] = useState<WeavePopNotification[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const remove = useCallback(
    (id: string) => {
      setQueue((prev) => {
        const newQueue = prev.filter((item) => item.id !== id);

        if (isExperimentalMode) {
          setActiveIndex((prevIndex) => (newQueue.length === 0 ? 0 : Math.min(prevIndex, newQueue.length - 1)));
        }

        return newQueue;
      });
    },
    [isExperimentalMode]
  );

  const add = useCallback(
    (notification: WeavePopNotification) => {
      if (isExperimentalMode) {
        setQueue((prev) => [...prev, notification]);
      } else {
        setQueue((prev) => [notification, ...prev]);
      }
    },
    [isExperimentalMode]
  );

  const removeAll = useCallback(() => {
    setActiveIndex(0);
    setQueue([]);
  }, []);

  const update = useCallback((notification: WeavePopNotification) => {
    setQueue((prev) => prev.map((p) => (p.id === notification.id ? ({ ...p, ...notification } as typeof p) : p)));
  }, []);

  return (
    <TransientQueueContext.Provider value={{ queue, add, remove, removeAll, update, setActiveIndex, activeIndex }}>
      {children}
    </TransientQueueContext.Provider>
  );
};

export { useTransientQueueContext as useTransientQueue };

type TransientItemContainerProps = {
  children: ReactNode;
  id: string;
  onClick?: (id: string) => void;
  xAlignment: 'left' | 'right';
} & CommonHTMLAttributes &
  MotionProps;

export const TransientItemContainer = forwardRef<HTMLDivElement, TransientItemContainerProps>(
  ({ children, id, onClick, xAlignment, ...rest }, ref) => {
    const { getFeatureFlagValue } = useScopedAppFlagStore();
    const isNewNotificationsExperience = getFeatureFlagValue('revamped-notifications-pops');
    const x = xAlignment === 'left' ? '-100%' : '100%';

    if (isNewNotificationsExperience) {
      return (
        <motion.div ref={ref} layout='position' onClick={() => onClick?.(id)} {...rest}>
          {children}
        </motion.div>
      );
    }

    return (
      <motion.div
        ref={ref}
        initial={{ x }}
        animate={{ x: 0 }}
        exit={{ x }}
        transition={{ ease: 'easeOut', duration: 0.1 }}
        onClick={() => onClick?.(id)}
        {...rest}
      >
        {children}
      </motion.div>
    );
  }
);
TransientItemContainer.displayName = 'TransientItemContainer';
